/**
 * MealForm component
 * Reusable form for creating and editing meals with tag management
 */

import { useForm } from '@tanstack/react-form';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import type { Meal } from '@/types/api';

export interface MealFormValues {
  name: string;
  tags: Array<{ tag: string; isHidden: boolean }>;
  repeatIntervalWeeks: number;
}

export interface MealFormProps {
  /** Initial values for editing (undefined for creation) */
  initialValues?: Meal;
  /** Whether form is in loading state */
  isLoading?: boolean;
  /** Submit handler */
  onSubmit: (values: MealFormValues) => void | Promise<void>;
  /** Cancel handler */
  onCancel: () => void;
  /** Submit button text */
  submitText?: string;
}

/**
 * Form component for creating and editing meals
 *
 * Features:
 * - Meal name input with validation
 * - Tag management (add/remove)
 * - Tag visibility toggles (hidden/visible)
 * - Real-time validation
 * - Loading states
 * - Accessible form controls
 *
 * @example
 * ```tsx
 * function CreateMealPage() {
 *   const createMutation = useCreateMeal();
 *   const navigate = useNavigate();
 *
 *   return (
 *     <MealForm
 *       onSubmit={async (values) => {
 *         const meal = await createMutation.mutateAsync({ name: values.name, tags: values.tags });
 *         navigate({ to: `/meals/${meal.id}` });
 *       }}
 *       onCancel={() => navigate({ to: '/meals' })}
 *       isLoading={createMutation.isPending}
 *       submitText="Create Meal"
 *     />
 *   );
 * }
 * ```
 */
export function MealForm({
  initialValues,
  isLoading = false,
  onSubmit,
  onCancel,
  submitText,
}: MealFormProps) {
  const { t } = useTranslation();

  // Convert MealTag[] to tag input format
  const initialTags = initialValues?.tags.map((t) => ({ tag: t.tag, isHidden: t.isHidden })) || [];

  // Local state for tag input
  const [tagInput, setTagInput] = useState('');

  const form = useForm({
    defaultValues: {
      name: initialValues?.name || '',
      tags: initialTags,
      repeatIntervalWeeks: initialValues?.repeatIntervalWeeks ?? 3,
    },
    onSubmit: async ({ value }: { value: MealFormValues }) => {
      await onSubmit(value);
    },
  });

  /**
   * Add new tag to the form
   */
  const handleAddTag = (isHidden: boolean) => {
    const trimmedTag = tagInput.trim();

    if (!trimmedTag) return;

    const currentTags = form.getFieldValue('tags') || [];

    // Check for duplicate tags (case-insensitive)
    const isDuplicate = currentTags.some(
      (t) => t.tag.toLowerCase() === trimmedTag.toLowerCase()
    );

    if (isDuplicate) {
      return; // Silently ignore duplicates
    }

    form.setFieldValue('tags', [...currentTags, { tag: trimmedTag, isHidden }]);
    setTagInput(''); // Clear input after adding
  };

  /**
   * Remove tag from the form
   */
  const handleRemoveTag = (index: number) => {
    const currentTags = form.getFieldValue('tags') || [];
    form.setFieldValue(
      'tags',
      currentTags.filter((_, i) => i !== index)
    );
  };

  /**
   * Toggle tag visibility
   */
  const handleToggleTagVisibility = (index: number) => {
    const currentTags = form.getFieldValue('tags') || [];
    const updatedTags = [...currentTags];
    updatedTags[index] = { ...updatedTags[index], isHidden: !updatedTags[index].isHidden };
    form.setFieldValue('tags', updatedTags);
  };

  const tags = form.getFieldValue('tags') || [];
  const nameValue = form.getFieldValue('name') || '';
  const repeatIntervalWeeks = form.getFieldValue('repeatIntervalWeeks') || 3;
  const isValidName = nameValue.trim().length >= 2 && nameValue.length <= 100;
  const isValidRepeatInterval =
    Number.isInteger(repeatIntervalWeeks) &&
    repeatIntervalWeeks >= 1 &&
    repeatIntervalWeeks <= 52;
  const hasFormErrors = form.state.errors.length > 0;

  // Determine if submit should be disabled
  const isSubmitDisabled =
    !isValidName || !isValidRepeatInterval || isLoading || hasFormErrors;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
      className="space-y-6"
    >
      {/* Meal Name Field */}
      <form.Field
        name="name"
        validators={{
          onChange: ({ value }) => {
            if (!value || value.trim().length === 0) {
              return t('meals.mealNameRequired');
            }
            if (value.trim().length < 2) {
              return t('meals.mealNameMinLength');
            }
            if (value.length > 100) {
              return t('meals.mealNameMaxLength');
            }
            return undefined;
          },
        }}
      >
        {(field) => (
          <div>
            <label htmlFor="meal-name" className="block text-sm font-medium text-gray-700">
              {t('meals.mealName')} <span className="text-red-500">*</span>
            </label>
            <input
              id="meal-name"
              type="text"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              disabled={isLoading}
              placeholder={t('meals.mealNamePlaceholder')}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              aria-invalid={field.state.meta.errors.length > 0}
              aria-describedby={field.state.meta.errors.length > 0 ? 'name-error' : undefined}
            />
            {field.state.meta.errors.length > 0 && (
              <p id="name-error" className="mt-1 text-sm text-red-600" role="alert">
                {field.state.meta.errors[0]}
              </p>
            )}
          </div>
        )}
      </form.Field>

      {/* Repeat Interval Field */}
      <form.Field
        name="repeatIntervalWeeks"
        validators={{
          onChange: ({ value }) => {
            if (!Number.isInteger(value) || value < 1 || value > 52) {
              return t('meals.repeatIntervalError');
            }
            return undefined;
          },
        }}
      >
        {(field) => (
          <div>
            <label
              htmlFor="repeat-interval-weeks"
              className="block text-sm font-medium text-gray-700"
            >
              {t('meals.repeatInterval')}
            </label>
            <div className="mt-1 flex items-center gap-3">
              <input
                id="repeat-interval-weeks"
                type="number"
                min={1}
                max={52}
                step={1}
                value={field.state.value}
                onChange={(e) =>
                  field.handleChange(Number.parseInt(e.target.value, 10) || 1)
                }
                onBlur={field.handleBlur}
                disabled={isLoading}
                className="w-28 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                aria-invalid={field.state.meta.errors.length > 0}
                aria-describedby={
                  field.state.meta.errors.length > 0
                    ? 'repeat-interval-error'
                    : 'repeat-interval-help'
                }
              />
              <span className="text-sm text-gray-600">
                {t('meals.repeatIntervalUnit')}
              </span>
            </div>
            <p id="repeat-interval-help" className="mt-1 text-xs text-gray-500">
              {t('meals.repeatIntervalDescription')}
            </p>
            {field.state.meta.errors.length > 0 && (
              <p
                id="repeat-interval-error"
                className="mt-1 text-sm text-red-600"
                role="alert"
              >
                {field.state.meta.errors[0]}
              </p>
            )}
          </div>
        )}
      </form.Field>

      {/* Tags Section */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{t('meals.tags')}</label>

        {/* Tag Input */}
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddTag(false);
              }
            }}
            disabled={isLoading}
            placeholder={t('meals.addTag')}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            aria-label={t('meals.tags')}
          />
          <Button
            type="button"
            onClick={() => handleAddTag(false)}
            disabled={!tagInput.trim() || isLoading}
            size="md"
            variant="outline"
          >
            {t('meals.addVisible')}
          </Button>
          <Button
            type="button"
            onClick={() => handleAddTag(true)}
            disabled={!tagInput.trim() || isLoading}
            size="md"
            variant="ghost"
          >
            {t('meals.addHidden')}
          </Button>
        </div>

        <p className="text-xs text-gray-500 mb-3">
          {t('meals.hiddenTagsDescription')}
        </p>

        {/* Tag List */}
        {tags.length > 0 ? (
          <div className="space-y-2">
            {tags.map((tag, index) => (
              <div
                key={index}
                className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2 border border-gray-200"
              >
                <div className="flex items-center gap-3 flex-1">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${
                      tag.isHidden
                        ? 'bg-gray-200 text-gray-700'
                        : 'bg-primary-100 text-primary-800'
                    }`}
                  >
                    {tag.tag}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleToggleTagVisibility(index)}
                    disabled={isLoading}
                    className="text-xs text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                    aria-label={t('meals.toggleVisibility', { tag: tag.tag })}
                  >
                    {tag.isHidden ? t('meals.hidden') : t('meals.visible')}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveTag(index)}
                  disabled={isLoading}
                  className="text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label={t('meals.removeTag', { tag: tag.tag })}
                >
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 italic">{t('meals.noTagsYet')}</p>
        )}
      </div>

      {/* Form Actions */}
      <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-200">
        <Button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          variant="outline"
          size="lg"
        >
          {t('buttons.cancel')}
        </Button>
        <button
          type="submit"
          disabled={isSubmitDisabled}
          className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-green-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-md"
          title={
            !isValidName
              ? t('meals.enterMealName')
              : hasFormErrors
              ? t('meals.fixValidationErrors')
              : isLoading
              ? t('meals.submitting')
              : t('meals.submitForm')
          }
        >
          {isLoading ? (
            <span className="flex items-center gap-2">
              <svg
                className="animate-spin h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              {t('app.saving')}
            </span>
          ) : (
            submitText || t('app.save')
          )}
        </button>
      </div>
    </form>
  );
}
